import fetch from "@/services/fetch";

function unwrapError(error) {
    if (error && error.response) {
        const data = error.response.data;
        if (typeof data === "string" && data) return data;
        if (data && data.title) return data.title;
        return error.response.statusText || "Request failed";
    }
    return (error && error.message) || "Request failed";
}

export default class PaymentAPI {
    constructor() { }

    /** Public: platform fee percent and the providers that are configured on the server. */
    config() {
        return fetch.get(`payment/config`).then(r => r.data);
    }

    // ----- host payout accounts -----

    accounts() {
        return fetch.get(`payment/accounts`).then(r => r.data).catch(e => { throw unwrapError(e); });
    }

    /** Returns the provider's hosted onboarding URL; the caller redirects the browser there. */
    connect(provider) {
        return fetch.post(`payment/accounts/${provider}/connect`).then(r => r.data.url).catch(e => { throw unwrapError(e); });
    }

    refreshAccount(id) {
        return fetch.post(`payment/accounts/${id}/refresh`).then(r => r.data).catch(e => { throw unwrapError(e); });
    }

    disconnect(id) {
        return fetch.delete(`payment/accounts/${id}`).then(r => r.data).catch(e => { throw unwrapError(e); });
    }

    /** Host view of a tournament's payment setup; platformFeePercent is present only for SuperAdmin. */
    paymentSettings(tournamentId) {
        return fetch.get(`payment/tournament/${tournamentId}/payment-settings`).then(r => r.data).catch(e => { throw unwrapError(e); });
    }

    /** { provider, paymentAccountId, feePayer: "Host"|"Buyer", paymentTestMode, platformFeePercent (SuperAdmin only) } */
    savePaymentSettings(tournamentId, settings) {
        return fetch.put(`payment/tournament/${tournamentId}/payment-settings`, settings)
            .then(r => r.data).catch(e => { throw unwrapError(e); });
    }

    // ----- platform (SuperAdmin) -----

    platformSettings() {
        return fetch.get(`payment/platform-settings`).then(r => r.data).catch(e => { throw unwrapError(e); });
    }

    savePlatformSettings(body) {
        return fetch.put(`payment/platform-settings`, body).then(r => r.data).catch(e => { throw unwrapError(e); });
    }

    clearTestOrders(tournamentId) {
        return fetch.delete(`payment/tournament/${tournamentId}/test-orders`).then(r => r.data).catch(e => { throw unwrapError(e); });
    }

    // ----- buyer -----

    /** { orderId, checkoutUrl, amountCents, platformFeeCents, provider } */
    checkout(tournamentId, playerIds, options = {}) {
        return fetch.post(`payment/checkout`, { tournamentId: parseInt(tournamentId), playerIds, ...options })
            .then(r => r.data).catch(e => { throw unwrapError(e); });
    }

    /** Not logged in: pay from the per-tournament pay page (QR / link). */
    checkoutGuest(tournamentId, playerIds, email) {
        return fetch.post(`payment/checkout/guest`, { tournamentId: parseInt(tournamentId), playerIds, email, source: "pay" })
            .then(r => r.data).catch(e => { throw unwrapError(e); });
    }

    payInfo(tournamentId) {
        return fetch.get(`payment/tournament/${tournamentId}/pay-info`).then(r => r.data).catch(e => { throw unwrapError(e); });
    }

    /** Logged in: my own registered players with payment status. */
    myPlayers(tournamentId) {
        return fetch.get(`payment/tournament/${tournamentId}/my-players`).then(r => r.data).catch(e => { throw unwrapError(e); });
    }

    searchPlayers(tournamentId, q) {
        return fetch.get(`payment/tournament/${tournamentId}/players`, { params: { q } }).then(r => r.data).catch(e => { throw unwrapError(e); });
    }

    /** Recorder/Admin: record an in-person (cash/check) payment. */
    markPaid(playerId, paid) {
        return fetch.put(`player/${playerId}/paid/${paid ? "true" : "false"}`).then(r => r.data).catch(e => { throw unwrapError(e); });
    }

    order(orderId, key) {
        return fetch.get(`payment/order/${orderId}`, { params: key ? { key } : {} }).then(r => r.data).catch(e => { throw unwrapError(e); });
    }

    verify(orderId, key) {
        return fetch.post(`payment/order/${orderId}/verify`, null, { params: key ? { key } : {} }).then(r => r.data).catch(e => { throw unwrapError(e); });
    }

    /** Test provider only. */
    testComplete(orderId, key, success) {
        return fetch.post(`payment/test/complete/${orderId}`, null, { params: { key, success } }).then(r => r.data).catch(e => { throw unwrapError(e); });
    }

    // ----- invoices (host-generated payments) -----

    /** Generates a payment with a provider checkout session; returns the order incl. checkoutUrl (for the QR) and publicKey. */
    createInvoice(tournamentId, { amountCents = null, description = "", playerIds = [], payerEmail = "" } = {}) {
        return fetch.post(`payment/tournament/${tournamentId}/invoice`, { amountCents, description, playerIds, payerEmail })
            .then(r => r.data).catch(e => { throw unwrapError(e); });
    }

    invoice(orderId, key) {
        return fetch.get(`payment/invoice/${orderId}`, { params: key ? { key } : {} }).then(r => r.data).catch(e => { throw unwrapError(e); });
    }

    payInvoice(orderId, key, email) {
        return fetch.post(`payment/invoice/${orderId}/pay`, { email }, { params: key ? { key } : {} }).then(r => r.data).catch(e => { throw unwrapError(e); });
    }

    /** Host records a cash or check payment (players or amount) with a note. No provider involved. */
    recordPayment(tournamentId, body) {
        return fetch.post(`payment/tournament/${tournamentId}/record`, body).then(r => r.data).catch(e => { throw unwrapError(e); });
    }

    updateNote(orderId, note) {
        return fetch.put(`payment/order/${orderId}/note`, { note }).then(r => r.data).catch(e => { throw unwrapError(e); });
    }

    invoiceCash(orderId) {
        return fetch.post(`payment/invoice/${orderId}/cash`).then(r => r.data).catch(e => { throw unwrapError(e); });
    }

    invoiceDiscard(orderId) {
        return fetch.delete(`payment/invoice/${orderId}`).then(r => r.data).catch(e => { throw unwrapError(e); });
    }

    // ----- host / admin -----

    summary(tournamentId) {
        return fetch.get(`payment/tournament/${tournamentId}/summary`).then(r => r.data).catch(e => { throw unwrapError(e); });
    }

    providerLink(tournamentId) {
        return fetch.get(`payment/tournament/${tournamentId}/provider-link`).then(r => r.data.url).catch(e => { throw unwrapError(e); });
    }

    resendReceipt(orderId) {
        return fetch.post(`payment/order/${orderId}/receipt`).then(r => r.data).catch(e => { throw unwrapError(e); });
    }

    refund(orderId, amountCents = null, refundPlatformFee = true) {
        return fetch.post(`payment/order/${orderId}/refund`, { amountCents, refundPlatformFee })
            .then(r => r.data).catch(e => { throw unwrapError(e); });
    }

    tournamentOrders(tournamentId) {
        return fetch.get(`payment/tournament/${tournamentId}/orders`).then(r => r.data).catch(e => { throw unwrapError(e); });
    }

    // ----- helpers -----

    static money(cents, currency = "USD") {
        return new Intl.NumberFormat("en-US", { style: "currency", currency }).format((cents || 0) / 100);
    }
}
